import { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CartContext from '../../context/CartContext';
import AuthContext from '../../context/AuthContext';

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, clearCart } = useContext(CartContext);
  const { isAuthenticated } = useContext(AuthContext);
  const [subtotal, setSubtotal] = useState(0);
  const [tax, setTax] = useState(0);
  const [shipping, setShipping] = useState(0);
  const navigate = useNavigate();
  
  // Calculate cart totals
  useEffect(() => {
    const calcSubtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setSubtotal(calcSubtotal);
    
    // Calculate tax (e.g., 10%)
    const calcTax = calcSubtotal * 0.1;
    setTax(calcTax);
    
    // Calculate shipping based on product settings
    let calcShipping = 0;
    
    // Check if ALL items have free shipping
    const allItemsHaveFreeShipping = cart.every(item => item.freeShipping === true);
    
    if (allItemsHaveFreeShipping) {
      // If all items have free shipping, shipping is always 0
      calcShipping = 0;
    } else if (calcSubtotal > 100) {
      // If subtotal is over $100, provide free shipping regardless of product settings
      calcShipping = 0;
    } else {
      // Some items don't have free shipping and order is under $100
      // Find items that don't have free shipping
      const nonFreeShippingItems = cart.filter(item => !item.freeShipping);
      
      if (nonFreeShippingItems.length > 0) {
        // Extract shipping fees, defaulting to 5.99 if not present
        const shippingFees = nonFreeShippingItems.map(item => {
          return item.shippingFee !== undefined ? parseFloat(item.shippingFee) : 0.00;
        });
        
        // Find the highest shipping fee
        calcShipping = Math.max(...shippingFees);
      }
    }
    
    setShipping(calcShipping);
  }, [cart]);
  
  const handleQuantityChange = (id, quantity) => {
    updateQuantity(id, quantity);
  };
  
  const handleRemoveItem = (id) => {
    removeFromCart(id);
  };
  
  const handleCheckout = () => {
    if (isAuthenticated) {
      navigate('/checkout');
    } else {
      navigate('/login', { state: { from: '/checkout' } });
    }
  };
  
  if (cart.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="neumorph p-8 rounded-lg inline-block">
          <svg className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h2 className="text-2xl font-bold mb-4">Your Cart is Empty</h2>
          <p className="text-gray-600 mb-6">Looks like you haven't added any products to your cart yet.</p>
          <Link to="/products" className="neumorph-btn px-6 py-3 inline-block">
            Start Shopping
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">Your Shopping Cart</h1>
      
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Cart Items */}
        <div className="lg:w-2/3">
          <div className="neumorph p-6 rounded-lg">
            <div className="hidden md:flex font-semibold text-gray-700 mb-4 pb-2 border-b">
              <div className="w-2/5">Product</div>
              <div className="w-1/5 text-center">Price</div>
              <div className="w-1/5 text-center">Quantity</div>
              <div className="w-1/5 text-center">Total</div>
            </div>
            
            {cart.map(item => (
              <div key={item._id} className="flex flex-col md:flex-row items-center py-4 border-b last:border-b-0">
                {/* Product */}
                <div className="w-full md:w-2/5 flex items-center mb-4 md:mb-0">
                  <div className="neumorph p-2 rounded-lg mr-4">
                    <img 
                      src={item.imageUrl} 
                      alt={item.name} 
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  </div>
                  <div>
                    <Link to={`/products/${item._id}`} className="font-semibold hover:text-blue-600">
                      {item.name}
                    </Link>
                    <button 
                      onClick={() => handleRemoveItem(item._id)}
                      className="text-red-500 text-sm hover:text-red-700 mt-1 block"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                
                {/* Price */}
                <div className="w-full md:w-1/5 text-center mb-4 md:mb-0">
                  <div className="md:hidden font-semibold text-gray-700 mb-1">Price:</div>
                  ${item.price.toFixed(2)}
                </div>
                
                {/* Quantity */}
                <div className="w-full md:w-1/5 text-center mb-4 md:mb-0">
                  <div className="md:hidden font-semibold text-gray-700 mb-1">Quantity:</div>
                  <div className="flex justify-center">
                    <button 
                      onClick={() => handleQuantityChange(item._id, item.quantity - 1)}
                      className="neumorph-btn px-2 py-1 text-sm"
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(item._id, parseInt(e.target.value) || 1)}
                      className="neumorph-inset w-12 mx-2 px-2 py-1 text-center rounded-lg focus:outline-none"
                      min="1"
                      max={item.stock}
                    />
                    <button 
                      onClick={() => handleQuantityChange(item._id, item.quantity + 1)}
                      className="neumorph-btn px-2 py-1 text-sm"
                      disabled={item.quantity >= item.stock}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                {/* Total */}
                <div className="w-full md:w-1/5 text-center">
                  <div className="md:hidden font-semibold text-gray-700 mb-1">Total:</div>
                  <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              </div>
            ))}
            
            <div className="flex justify-between mt-6">
              <button 
                onClick={() => clearCart()}
                className="neumorph-btn px-4 py-2 text-sm"
              >
                Clear Cart
              </button>
              <Link to="/products" className="neumorph-btn px-4 py-2 text-sm">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
        
        {/* Order Summary */}
        <div className="lg:w-1/3">
          <div className="neumorph p-6 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax (10%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span>{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
              </div>
              {subtotal < 100 && !cart.every(item => item.freeShipping) && (
                <div className="text-sm text-green-600 mt-2">
                  Add ${(100 - subtotal).toFixed(2)} more to get free shipping!
                </div>
              )}
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>${(subtotal + tax + shipping).toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <button 
              onClick={handleCheckout}
              className="neumorph-btn w-full py-3 font-semibold"
            >
              Proceed to Checkout
            </button>
            
            <div className="mt-6 text-sm text-gray-600">
              <p className="mb-2">We accept:</p>
              <div className="flex space-x-2">
                <div className="neumorph-inset px-2 py-1 rounded-md">PayHere</div>
                <div className="neumorph-inset px-2 py-1 rounded-md">2Checkout</div>
                <div className="neumorph-inset px-2 py-1 rounded-md">COD</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
